import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/* Encabezado de página — título grande + descripción + acciones al lado */
export function DashHeading({
  title,
  description,
  actions,
  className,
}: {
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mb-8", className)}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-3xl xl:text-[32px] font-semibold tracking-tight text-neutral-900 leading-tight">
            {title}
          </h1>
          {description && (
            <p className="mt-2 text-sm text-neutral-600 max-w-2xl leading-relaxed">
              {description}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-wrap">{actions}</div>
        )}
      </div>
    </div>
  );
}

/* Título de sección — para agrupar KPI cards por tema */
export function SectionTitle({
  children,
  hint,
  className,
}: {
  children: React.ReactNode;
  hint?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mb-4 flex items-baseline justify-between gap-4", className)}>
      <h2 className="text-lg font-semibold text-neutral-900 tracking-tight">
        {children}
      </h2>
      {hint && <span className="text-xs text-neutral-500">{hint}</span>}
    </div>
  );
}

/* KPI card estilo RevenueCat en light mode.
 * - Título arriba
 * - Icono opcional arriba a la derecha
 * - Número grande al medio
 * - Hint pequeño abajo
 */
export function MetricCard({
  title,
  value,
  hint,
  icon,
  className,
}: {
  title: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border border-neutral-200 bg-white p-6",
        "flex flex-col justify-between min-h-[148px]",
        "transition-colors hover:border-neutral-300",
        className
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm font-medium text-neutral-600">{title}</div>
        {icon && (
          <div className="text-neutral-400 flex-shrink-0">{icon}</div>
        )}
      </div>
      <div className="mt-4">
        <div className="text-[40px] font-semibold tracking-tight text-neutral-900 tabular-nums leading-none">
          {value}
        </div>
        {hint && (
          <div className="mt-2 text-xs text-neutral-500 leading-tight">{hint}</div>
        )}
      </div>
    </div>
  );
}

/* Pills segmentados para filtrar (ej: Todos | Activos | Anulados). Estilo
 * RevenueCat en light mode: pill activo con fondo blanco + borde + sombra,
 * inactivos texto gris hover. */
export function PillTabs<T extends string>({
  value,
  onChange,
  options,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string; count?: number }[];
  className?: string;
}) {
  return (
    <div
      className={cn(
        "inline-flex items-center gap-1 rounded-lg bg-neutral-100 p-1",
        className
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[13px] font-medium tracking-tight transition-all",
              active
                ? "bg-white text-neutral-900 shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                : "text-neutral-600 hover:text-neutral-900"
            )}
          >
            {opt.label}
            {opt.count != null && (
              <span
                className={cn(
                  "text-[11px] tabular-nums",
                  active ? "text-neutral-500" : "text-neutral-400"
                )}
              >
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* Ícono de info al lado de un label (ej: en encabezados de tabla) que abre
 * un popover con explicaciones. Uso: <ColumnInfo title="Estado" items={[...]} />
 * Cada item tiene un label (ej: "Activo") y un description (ej: "código emitido...").
 */
export function ColumnInfo({
  title,
  items,
}: {
  title: string;
  items: { label: string; description: React.ReactNode }[];
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center h-4 w-4 rounded-full text-neutral-400 hover:text-neutral-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-300"
          aria-label={`Info sobre ${title}`}
        >
          <Info className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-4">
        <div className="text-sm font-semibold text-neutral-900 mb-2">{title}</div>
        <dl className="space-y-2">
          {items.map((item) => (
            <div key={item.label}>
              <dt className="text-xs font-semibold text-neutral-800">{item.label}</dt>
              <dd className="text-xs text-neutral-600 leading-relaxed mt-0.5">
                {item.description}
              </dd>
            </div>
          ))}
        </dl>
      </PopoverContent>
    </Popover>
  );
}

/* Contenedor genérico con título opcional — para tablas, listas o contenido
 * agrupado que no es un solo número */
export function DashCard({
  title,
  action,
  children,
  className,
  bodyClassName,
}: {
  title?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border border-neutral-200 bg-white",
        className
      )}
    >
      {(title || action) && (
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-neutral-200">
          {title && (
            <div className="text-sm font-semibold text-neutral-900">{title}</div>
          )}
          {action}
        </div>
      )}
      <div className={cn("p-6", bodyClassName)}>{children}</div>
    </div>
  );
}
